import React, { useState, useEffect } from "react";

export default function GestionPerfiles() {
  const [cedulaBusqueda, setCedulaBusqueda] = useState("");
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [cargoOriginalInfo, setCargoOriginalInfo] = useState("");
  const [listaSedes, setListaSedes] = useState([]);
  const [listaCargos, setListaCargos] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    nombres: "",
    apellidos: "",
    documento: "",
    correo: "",
    cargo: "",
    estado: "Activo",
    esSuperAdmin: false,
    accesoGlobalSedes: false,
    nuevaPassword: "",
    codigo_bodega: "",
    centro_trabajo: ""
  });

  useEffect(() => {
    cargarSedesYCargos();
  }, []);

  const cargarSedesYCargos = async () => {
    try {
      const [resSedes, resCargos] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/sedes`),
        fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/cargos`)
      ]);
      const dataSedes = await resSedes.json();
      const dataCargos = await resCargos.json();
      if (dataSedes.success) setListaSedes(dataSedes.data || []);
      if (dataCargos.success) setListaCargos(dataCargos.cargos || []);
    } catch (err) {
      console.error("Error cargando sedes o cargos:", err);
    }
  };

  const handleBuscarUsuario = async (e) => {
    e.preventDefault();
    if (!cedulaBusqueda.trim()) return;
    setBuscando(true);
    setMensaje({ text: "", type: "" });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/buscar?query=${cedulaBusqueda.trim()}`);
      const data = await res.json();
      const userObj = Array.isArray(data) ? data[0] : (data.usuario || data);

      if (userObj && userObj.documento) {
        setUsuarioEncontrado(userObj);
        const cargoReal = userObj.cargo_usuario || userObj.empl_cargo || userObj.cargo || "SIN CARGO";
        setCargoOriginalInfo(cargoReal);
        const esSuper = String(cargoReal).toUpperCase().includes("SUPER_ADMIN");
        const estadoBD = String(userObj.estado || "activo").toLowerCase();

        // Separar nombres y apellidos si vienen juntos
        const partesNombre = String(userObj.nombres || "").trim().split(" ");
        const soloNom = userObj.solo_nombres || partesNombre.slice(0, 2).join(" ") || "";
        const soloApe = userObj.solo_apellidos || partesNombre.slice(2).join(" ") || "";

        setFormData({
          nombres: soloNom,
          apellidos: soloApe,
          documento: userObj.documento || cedulaBusqueda.trim(),
          correo: userObj.correo || "",
          cargo: esSuper ? "SUPER_ADMIN" : cargoReal,
          estado: estadoBD === "inactivo" ? "Inactivo" : "Activo",
          esSuperAdmin: esSuper,
          accesoGlobalSedes: Boolean(userObj.acceso_global_sedes || esSuper),
          nuevaPassword: "",
          codigo_bodega: userObj.codigo_bodega || "",
          centro_trabajo: userObj.sede || userObj.area_nombre || ""
        });
      } else {
        setMensaje({ text: "Colaborador no encontrado en la base de datos.", type: "error" });
        setUsuarioEncontrado(null);
      }
    } catch (err) {
      setMensaje({ text: "Error de conexión al buscar el usuario.", type: "error" });
      setUsuarioEncontrado(null);
    } finally {
      setBuscando(false);
    }
  };

  const handleToggleSuperAdmin = (e) => {
    const isChecked = e.target.checked;
    setFormData({
      ...formData,
      esSuperAdmin: isChecked,
      accesoGlobalSedes: isChecked ? true : formData.accesoGlobalSedes,
      cargo: isChecked ? "SUPER_ADMIN" : cargoOriginalInfo
    });
  };

  const handleGuardarCambios = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setMensaje({ text: "", type: "" });

    try {
      const docActual = formData.documento || cedulaBusqueda.trim();
      const payloadEnvio = {
        id: usuarioEncontrado.id,
        nombres: formData.nombres,
        apellidos: formData.apellidos,
        correo: formData.correo,
        cargo: formData.esSuperAdmin ? "SUPER_ADMIN" : formData.cargo,
        estado: formData.estado.toLowerCase(),
        is_active: formData.estado.toLowerCase() === "activo" ? 1 : 0,
        acceso_global_sedes: formData.accesoGlobalSedes ? 1 : 0,
        newPassword: formData.nuevaPassword,
        codigo_bodega: formData.codigo_bodega,
        centro_trabajo: formData.centro_trabajo
      };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/${docActual}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadEnvio)
      });
      const data = await res.json();

      if (data.success) {
        setMensaje({ text: "¡Perfil y credenciales actualizados correctamente! ✅", type: "success" });
        setFormData(prev => ({ ...prev, nuevaPassword: "" }));
      } else {
        setMensaje({ text: data.message || "Error al actualizar el perfil.", type: "error" });
      }
    } catch (error) {
      setMensaje({ text: "Error al conectar con el servidor para actualizar.", type: "error" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-8 animate-fade-in">
      {/* COLUMNA IZQUIERDA: Buscador y Resumen */}
      <div className="w-full lg:w-1/3 flex flex-col gap-6">
        <div className="bg-slate-50/80 p-6 rounded-3xl border border-slate-200/80 shadow-sm">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">1. Buscar Colaborador</h3>
          <form onSubmit={handleBuscarUsuario} className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Número de Documento / Cédula</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cedulaBusqueda}
                  onChange={(e) => setCedulaBusqueda(e.target.value)}
                  placeholder="Ej: 10697..."
                  className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                />
                <button
                  type="submit"
                  disabled={buscando}
                  className="px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-wider rounded-xl transition shadow-md cursor-pointer disabled:opacity-50"
                >
                  {buscando ? "..." : "Buscar"}
                </button>
              </div>
            </div>
          </form>

          {usuarioEncontrado && (
            <div className="mt-6 pt-6 border-t border-slate-200/60 flex items-center gap-4 animate-fade-in">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-black text-2xl shadow-md">
                {String(usuarioEncontrado.nombres || "U").charAt(0).toUpperCase()}
              </div>
              <div>
                <span className="inline-block text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 mb-1">
                  ✓ Registro Verificado (ID: {usuarioEncontrado.id || "BD"})
                </span>
                <h4 className="text-sm font-black text-slate-800">{usuarioEncontrado.nombres}</h4>
                <p className="text-[11px] font-bold text-indigo-600 uppercase">{formData.cargo}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* COLUMNA DERECHA: Formulario de Edición */}
      <div className="w-full lg:w-2/3 bg-white/70 backdrop-blur-xl p-8 rounded-[32px] border border-white shadow-xl">
        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">2. Configuración de Perfil y Credenciales</h3>

        {mensaje.text && (
          <div className={`mb-6 p-4 rounded-2xl text-xs font-bold flex items-center gap-3 animate-fade-in-down ${mensaje.type === "success" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}>
            <span className="text-lg">{mensaje.type === "success" ? "✅" : "⚠️"}</span> {mensaje.text}
          </div>
        )}

        {!usuarioEncontrado ? (
          <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center">
            <span className="text-5xl mb-3 opacity-40">👤</span>
            <p className="text-sm font-bold">Busca un colaborador por cédula en el panel izquierdo para editar su perfil.</p>
          </div>
        ) : (
          <form onSubmit={handleGuardarCambios} className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Cédula / Documento</label>
                <input type="text" value={formData.documento} disabled className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 font-bold text-xs cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Correo Electrónico</label>
                <input type="email" value={formData.correo} onChange={(e) => setFormData({ ...formData, correo: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-bold text-xs focus:outline-none focus:border-indigo-500 shadow-sm" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Nombres</label>
                <input type="text" value={formData.nombres} onChange={(e) => setFormData({ ...formData, nombres: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-bold text-xs focus:outline-none focus:border-indigo-500 shadow-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Apellidos</label>
                <input type="text" value={formData.apellidos} onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-bold text-xs focus:outline-none focus:border-indigo-500 shadow-sm" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Cargo en Sistema</label>
                <input
                  type="text"
                  list="cargos-list"
                  value={formData.cargo}
                  onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-bold text-xs focus:outline-none focus:border-indigo-500 shadow-sm uppercase"
                />
                <datalist id="cargos-list">
                  {listaCargos.map((c, i) => <option key={i} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Estado de Cuenta</label>
                <select value={formData.estado} onChange={(e) => setFormData({ ...formData, estado: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-bold text-xs focus:outline-none focus:border-indigo-500 shadow-sm cursor-pointer">
                  <option value="Activo">🟢 Activo</option>
                  <option value="Inactivo">🔴 Inactivo</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_100px] gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Sede / Centro de Trabajo Asignado</label>
                <select
                  value={formData.codigo_bodega}
                  onChange={(e) => {
                    const cod = e.target.value;
                    const sedeObj = listaSedes.find(s => String(s.codigo_bodega) === String(cod));
                    setFormData({ ...formData, codigo_bodega: cod, centro_trabajo: sedeObj ? sedeObj.nombre : "" });
                  }}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-bold text-xs focus:outline-none focus:border-indigo-500 shadow-sm cursor-pointer"
                >
                  <option value="">-- Seleccione una sede --</option>
                  {listaSedes.map((sede) => (
                    <option key={sede.id} value={sede.codigo_bodega}>{sede.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 text-center">Cód. Bodega</label>
                <input type="text" value={formData.codigo_bodega} readOnly className="w-full px-3 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 font-black text-xs text-center" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <label className="bg-slate-50 border border-slate-200 p-4 rounded-2xl flex items-center justify-between cursor-pointer hover:bg-indigo-50/40 transition">
                <div>
                  <p className="text-xs font-black text-slate-800">🌍 Acceso Global a Sedes</p>
                  <p className="text-[10px] text-slate-500 font-semibold mt-0.5">Permite supervisar registros de todas las sedes.</p>
                </div>
                <input type="checkbox" checked={formData.accesoGlobalSedes} onChange={(e) => setFormData({ ...formData, accesoGlobalSedes: e.target.checked })} className="w-5 h-5 accent-indigo-600 rounded cursor-pointer" />
              </label>

              <label className="bg-slate-50 border border-slate-200 p-4 rounded-2xl flex items-center justify-between cursor-pointer hover:bg-indigo-50/40 transition">
                <div>
                  <p className="text-xs font-black text-slate-800">🛡️ Rol Super Admin</p>
                  <p className="text-[10px] text-slate-500 font-semibold mt-0.5">Asigna cargo SUPER_ADMIN con control total.</p>
                </div>
                <input type="checkbox" checked={formData.esSuperAdmin} onChange={handleToggleSuperAdmin} className="w-5 h-5 accent-indigo-600 rounded cursor-pointer" />
              </label>
            </div>

            <div className="bg-amber-50/70 border border-amber-200/80 p-4 rounded-2xl">
              <label className="block text-[10px] font-black text-amber-800 uppercase tracking-widest mb-1.5">🔑 Asignar Nueva Contraseña (Opcional)</label>
              <input
                type="password"
                value={formData.nuevaPassword}
                onChange={(e) => setFormData({ ...formData, nuevaPassword: e.target.value })}
                placeholder="Dejar en blanco para mantener la contraseña actual..."
                className="w-full px-4 py-2.5 bg-white border border-amber-300 rounded-xl text-slate-800 text-xs font-bold focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                type="submit"
                disabled={guardando}
                className="btn-glow px-8 py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-500/30 transition cursor-pointer disabled:opacity-50"
              >
                {guardando ? "Guardando..." : "💾 Guardar Cambios del Perfil"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}